/**
 * @fileoverview User Profile Hook
 * 
 * Provides CRUD operations for user profiles including avatar and resume uploads.
 * This is the primary hook for managing user profile data throughout the application.
 * 
 * @module hooks/useProfile
 * @version 1.0.0
 * @since 2024-01-01
 * 
 * @architecture
 * ```
 * useProfile (this hook)
 *     │
 *     ├── profiles table        ← Core profile data
 *     ├── profile-avatars bucket ← Avatar storage
 *     └── resumes bucket        ← Resume storage
 * ```
 * 
 * @exports
 * - {@link useProfile} - Fetch profile by user ID
 * - {@link useUpdateProfile} - Update profile fields
 * - {@link useUploadAvatar} - Upload profile picture
 * - {@link useUploadResume} - Upload resume document
 * 
 * @example
 * // Fetch and display profile
 * function ProfileCard({ userId }) {
 *   const { data: profile, isLoading } = useProfile(userId);
 *   
 *   if (isLoading) return <Skeleton />;
 *   
 *   return (
 *     <Card>
 *       <Avatar src={profile?.avatar_url} />
 *       <h2>{profile?.full_name}</h2>
 *       <p>{profile?.professional_title}</p>
 *     </Card>
 *   );
 * }
 * 
 * @see {@link useCurrentUser} - Get authenticated user
 * @see {@link useWorkHistory} - Work experience data
 * @see {@link useCredentials} - Certifications and licenses
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useRecalculateScore } from "./useProfileScore";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Complete user profile data structure.
 * 
 * @interface Profile
 * @property {string} id - UUID primary key (matches auth.users.id)
 * @property {string | null} email - User email address
 * @property {string | null} full_name - Display name
 * @property {string | null} phone - Contact phone number
 * @property {string | null} avatar_url - Profile picture URL
 * @property {string | null} bio - Short biography
 * @property {string | null} headline - Professional headline
 * @property {string | null} location - General location (city, state)
 * @property {string | null} linkedin_url - LinkedIn profile URL
 * @property {string | null} professional_title - Job title
 * @property {string | null} status - Profile status (active, inactive, etc.)
 * @property {string | null} resume_url - Uploaded resume URL
 * @property {number | null} reliability_rating - Calculated reliability score
 * @property {string[] | null} areas_of_expertise - Skill areas
 * @property {any} linkedin_data - Synced LinkedIn profile data
 * @property {string | null} linkedin_synced_at - Last LinkedIn sync timestamp
 * @property {boolean | null} resume_ai_enhanced - Whether resume was AI-optimized
 * @property {number | null} profile_strength - Calculated profile completeness score
 * @property {string} created_at - Record creation timestamp
 * @property {string} updated_at - Last update timestamp
 * 
 * @description
 * Also includes optional fields for:
 * - Unified Identity (handle, nex_id)
 * - Address information (residential, mailing)
 * - Emergency contact
 * - Identity verification
 * - Legal name
 * - Privacy settings
 * - Global preferences
 */
export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  bio: string | null;
  headline: string | null;
  location: string | null;
  linkedin_url: string | null;
  professional_title: string | null;
  status: string | null;
  resume_url: string | null;
  reliability_rating: number | null;
  areas_of_expertise: string[] | null;
  linkedin_data: any;
  linkedin_synced_at: string | null;
  resume_ai_enhanced: boolean | null;
  profile_strength: number | null;
  created_at: string;
  updated_at: string;
  // Unified Identity Fields
  handle?: string | null;
  nex_id?: string | null;
  // Address fields
  residential_address?: string | null;
  residential_city?: string | null;
  residential_state?: string | null;
  residential_country?: string | null;
  residential_postal_code?: string | null;
  mailing_address?: string | null;
  mailing_city?: string | null;
  mailing_state?: string | null;
  mailing_country?: string | null;
  mailing_postal_code?: string | null;
  same_as_residential?: boolean | null;
  // Emergency contact
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  emergency_contact_relationship?: string | null;
  // Identity verification
  identity_verified?: boolean | null;
  identity_verified_at?: string | null;
  government_id_type?: string | null;
  date_of_birth?: string | null;
  // Legal name
  legal_first_name?: string | null;
  legal_last_name?: string | null;
  // Privacy settings
  profile_visibility?: string | null;
  show_email?: boolean | null;
  show_phone?: boolean | null;
  // Global preferences
  timezone?: string | null;
  preferences?: Record<string, any> | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches a user profile by ID.
 * 
 * @param {string | null} userId - The user ID to fetch profile for
 * @returns {UseQueryResult<Profile | null>} React Query result with profile data
 * 
 * @example
 * const { data: profile, isLoading, error } = useProfile(userId);
 * 
 * if (profile?.identity_verified) {
 *   // Show verified badge
 * }
 */
export function useProfile(userId: string | null) {
  return useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) throw error;
      return data as Profile | null;
    },
    enabled: !!userId,
  });
}

/**
 * Mutation hook for updating profile fields.
 * 
 * Automatically recalculates profile score after successful update
 * and invalidates the profile cache.
 * 
 * @returns {UseMutationResult} Mutation object with mutate function
 * 
 * @example
 * const { mutate: updateProfile, isPending } = useUpdateProfile();
 * 
 * updateProfile({
 *   userId: user.id,
 *   updates: {
 *     professional_title: 'Senior Developer',
 *     location: 'San Francisco, CA',
 *   }
 * });
 * 
 * @sideEffects
 * - Invalidates profile query cache
 * - Triggers profile score recalculation
 * - Shows success/error toast
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { mutate: recalculateScore } = useRecalculateScore();

  return useMutation({
    mutationFn: async ({
      userId,
      updates,
    }: {
      userId: string;
      updates: Partial<Profile>;
    }) => {
      const { data, error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["profile", data.id] });
      recalculateScore({ userId: data.id });
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Mutation hook for uploading profile avatar.
 * 
 * @returns {UseMutationResult} Mutation object with mutate function
 * 
 * @validation
 * - Max file size: 2MB
 * - Allowed types: JPEG, PNG, WEBP, GIF
 * 
 * @process
 * 1. Validates file size and type
 * 2. Deletes existing avatar if present
 * 3. Uploads new avatar to profile-avatars bucket
 * 4. Updates profile with new avatar URL
 * 
 * @example
 * const { mutate: uploadAvatar, isPending } = useUploadAvatar();
 * 
 * const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0];
 *   if (file) {
 *     uploadAvatar({ userId: user.id, file });
 *   }
 * };
 * 
 * @throws {Error} 'File size must be less than 2MB'
 * @throws {Error} 'File must be JPG, PNG, WEBP or GIF'
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      file,
    }: {
      userId: string;
      file: File;
    }) => {
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error("File size must be less than 2MB");
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        throw new Error("File must be JPG, PNG, WEBP or GIF");
      }

      const fileExt = file.name.split(".").pop();
      const fileName = `avatar.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      // Delete old avatar if exists
      const { data: existingFiles } = await supabase.storage
        .from("profile-avatars")
        .list(userId);

      if (existingFiles && existingFiles.length > 0) {
        await supabase.storage
          .from("profile-avatars")
          .remove(existingFiles.map(f => `${userId}/${f.name}`));
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from("profile-avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("profile-avatars")
        .getPublicUrl(filePath);

      // Update profile
      const { data, error } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["profile", data.id] });
      toast({
        title: "Success!",
        description: "Your profile picture has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Mutation hook for uploading resume document.
 * 
 * @returns {UseMutationResult} Mutation object with mutate function
 * 
 * @process
 * 1. Uploads file to resumes bucket with upsert
 * 2. Gets public URL for the uploaded file
 * 3. Updates profile.resume_url
 * 
 * @example
 * const { mutate: uploadResume, isPending } = useUploadResume();
 * 
 * uploadResume({
 *   userId: user.id,
 *   file: resumeFile, // PDF, DOC, DOCX
 * });
 * 
 * @sideEffects
 * - Stores file in 'resumes' storage bucket
 * - Updates profile.resume_url
 * - Invalidates profile cache
 */
export function useUploadResume() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      file,
    }: {
      userId: string;
      file: File;
    }) => {
      const fileExt = file.name.split(".").pop();
      const filePath = `${userId}/resume.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("resumes")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("resumes")
        .getPublicUrl(filePath);

      const { data, error } = await supabase
        .from("profiles")
        .update({ resume_url: publicUrl })
        .eq("id", userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["profile", data.id] });
      toast({
        title: "Resume uploaded",
        description: "Your resume has been uploaded successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
